// Shared between the public Careers pages and the admin, so labels, question
// wording and screening rules stay in sync.

export const CONTRACT_TYPES = [
  { value: 'cdi', fr: 'CDI', en: 'Permanent', schema: 'FULL_TIME' },
  { value: 'cdd', fr: 'CDD', en: 'Fixed-term', schema: 'TEMPORARY' },
  { value: 'internship', fr: 'Stage', en: 'Internship', schema: 'INTERN' },
  { value: 'apprenticeship', fr: 'Alternance', en: 'Work-study', schema: 'OTHER' },
  { value: 'freelance', fr: 'Freelance', en: 'Freelance', schema: 'CONTRACTOR' }
];

export const WORKPLACE_OPTIONS = [
  { value: 'onsite', fr: 'Sur site', en: 'On-site' },
  { value: 'hybrid', fr: 'Hybride', en: 'Hybrid' },
  { value: 'remote', fr: 'Télétravail', en: 'Remote' }
];

export const DEGREES = [
  { value: 'bac', fr: 'Baccalauréat', en: 'High school diploma' },
  { value: 'bac2', fr: 'Bac+2 (BTS, DUT)', en: 'Associate degree (2 years)' },
  { value: 'bac3', fr: 'Bac+3 (Licence, Bachelor)', en: "Bachelor's degree" },
  { value: 'bac5', fr: 'Bac+5 (Master, école d\'ingénieur)', en: "Master's degree" },
  { value: 'phd', fr: 'Doctorat', en: 'Doctorate' }
];

export const LANGUAGE_LEVELS = [
  { value: 'none', fr: 'Aucune', en: 'None' },
  { value: 'conversational', fr: 'Conversationnel', en: 'Conversational' },
  { value: 'professional', fr: 'Professionnel', en: 'Professional' },
  { value: 'native', fr: 'Natif ou bilingue', en: 'Native or bilingual' }
];

export const optionLabel = (options, value, lang) => {
  const option = options.find(o => o.value === value);
  return option ? option[lang === 'en' ? 'en' : 'fr'] : '';
};

/*
 * Screening question categories, modelled on LinkedIn's.
 * - answer: 'yesno' | 'number' (years) | 'level' (language proficiency)
 * - param: what the admin fills in (skill, language…), with its input type
 * - multiple: whether the category can be added more than once to a job
 * Wording uses {param}; {location} is replaced by the job location.
 */
export const QUESTION_CATEGORIES = [
  {
    key: 'driversLicense', label: "Driver's License", answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Avez-vous un permis de conduire valide ?',
    en: "Do you have a valid driver's license?"
  },
  {
    key: 'education', label: 'Education', answer: 'yesno', defaultIdeal: 'yes', multiple: true,
    param: { type: 'degree', label: 'Degree', default: 'bac5' },
    fr: "Avez-vous obtenu le niveau d'études suivant : {param} ?",
    en: 'Have you completed the following level of education: {param}?'
  },
  {
    key: 'skill', label: 'Expertise with Skill', answer: 'number', defaultIdeal: 2, multiple: true,
    param: { type: 'text', label: 'Skill', placeholder: 'e.g. UiPath, Power Automate, Python' },
    fr: "Combien d'années d'expérience avez-vous avec {param} ?",
    en: 'How many years of experience do you have with {param}?'
  },
  {
    key: 'hybrid', label: 'Hybrid Work', answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Êtes-vous à l\'aise avec un mode de travail hybride ?',
    en: 'Are you comfortable working in a hybrid setting?'
  },
  {
    key: 'industry', label: 'Industry Experience', answer: 'number', defaultIdeal: 2, multiple: true,
    param: { type: 'text', label: 'Industry', placeholder: 'e.g. banque / banking' },
    fr: "Combien d'années d'expérience avez-vous dans le secteur suivant : {param} ?",
    en: 'How many years of experience do you have in the following industry: {param}?'
  },
  {
    key: 'language', label: 'Language', answer: 'level', defaultIdeal: 'professional', multiple: true,
    param: { type: 'text', label: 'Language', placeholder: 'e.g. anglais / English' },
    fr: 'Quel est votre niveau en {param} ?',
    en: 'What is your level of proficiency in {param}?'
  },
  {
    key: 'location', label: 'Location', answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Le trajet jusqu\'au lieu de travail ({location}) vous convient-il ?',
    en: "Are you comfortable commuting to this job's location ({location})?"
  },
  {
    key: 'onsite', label: 'Onsite Work', answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Êtes-vous à l\'aise avec un travail sur site ?',
    en: 'Are you comfortable working in an onsite setting?'
  },
  {
    key: 'remote', label: 'Remote Work', answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Êtes-vous à l\'aise avec le télétravail ?',
    en: 'Are you comfortable working in a remote setting?'
  },
  {
    key: 'urgent', label: 'Urgent Hiring Need', answer: 'yesno', defaultIdeal: 'yes',
    fr: 'Ce poste est à pourvoir rapidement. Pouvez-vous démarrer immédiatement ?',
    en: 'We must fill this position urgently. Can you start immediately?'
  },
  {
    key: 'visa', label: 'Visa Status', answer: 'yesno', defaultIdeal: 'no',
    fr: 'Aurez-vous besoin, maintenant ou à l\'avenir, d\'un parrainage pour obtenir un visa de travail ?',
    en: 'Will you now or in the future require sponsorship for employment visa status?'
  },
  {
    key: 'workAuthorization', label: 'Work Authorization', answer: 'yesno', defaultIdeal: 'yes',
    param: { type: 'text', label: 'Country', default: 'France' },
    fr: 'Êtes-vous légalement autorisé(e) à travailler dans le pays suivant : {param} ?',
    en: 'Are you legally authorized to work in {param}?'
  },
  {
    key: 'workExperience', label: 'Work Experience', answer: 'number', defaultIdeal: 3, multiple: true,
    param: { type: 'text', label: 'Job function', placeholder: 'e.g. consultant RPA / RPA consultant' },
    fr: "Combien d'années d'expérience avez-vous dans la fonction suivante : {param} ?",
    en: 'How many years of experience do you have in the following role: {param}?'
  },
  {
    key: 'custom', label: 'Custom Question', answer: 'custom', defaultIdeal: 'yes', multiple: true,
    param: { type: 'text', label: 'Question', placeholder: 'Write your question' }
  }
];

export const MAX_QUESTIONS = 15;

export const categoryOf = (question) => QUESTION_CATEGORIES.find(c => c.key === question.category);

// Custom questions choose their own answer type (yes/no or number)
export const answerTypeOf = (question) => {
  const category = categoryOf(question);
  if (!category) return 'yesno';
  return category.answer === 'custom' ? (question.answerType || 'yesno') : category.answer;
};

export const newQuestion = (category) => ({
  id: `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  category: category.key,
  param: category.param?.default || '',
  answerType: category.answer === 'custom' ? 'yesno' : undefined,
  idealAnswer: category.defaultIdeal,
  required: false
});

// Question wording in the visitor's language; admin-entered params stay as written
export const questionText = (question, lang, job = {}) => {
  const category = categoryOf(question);
  if (!category) return '';
  if (category.key === 'custom') return question.param || '';
  const l = lang === 'en' ? 'en' : 'fr';
  const param = category.param?.type === 'degree'
    ? optionLabel(DEGREES, question.param, l)
    : (question.param || '');
  return category[l]
    .replace('{param}', param)
    .replace('{location}', job.location || (l === 'fr' ? 'à préciser' : 'to be confirmed'));
};

export const formatAnswer = (question, answer, lang) => {
  if (answer === undefined || answer === null || answer === '') return '—';
  const type = answerTypeOf(question);
  const l = lang === 'en' ? 'en' : 'fr';
  if (type === 'yesno') return answer === 'yes' ? (l === 'fr' ? 'Oui' : 'Yes') : (l === 'fr' ? 'Non' : 'No');
  if (type === 'level') return optionLabel(LANGUAGE_LEVELS, answer, l);
  return `${answer} ${l === 'fr' ? 'an(s)' : 'year(s)'}`;
};

// Does the candidate's answer meet the expected one?
export const meetsIdeal = (question, answer) => {
  const type = answerTypeOf(question);
  if (type === 'number') return Number(answer) >= Number(question.idealAnswer || 0);
  if (type === 'level') {
    const rank = (v) => LANGUAGE_LEVELS.findIndex(l => l.value === v);
    return rank(answer) >= rank(question.idealAnswer);
  }
  return answer === question.idealAnswer;
};

// Plain-text description -> blocks of paragraphs and bullet lists ("- " or "• " lines)
export const descriptionBlocks = (text) => {
  const blocks = [];
  (text || '').split('\n').forEach(raw => {
    const line = raw.trim();
    const bullet = /^[-•*]\s+/.test(line);
    const last = blocks[blocks.length - 1];
    if (!line) {
      blocks.push(null);
    } else if (bullet) {
      if (last && last.type === 'list') last.items.push(line.replace(/^[-•*]\s+/, ''));
      else blocks.push({ type: 'list', items: [line.replace(/^[-•*]\s+/, '')] });
    } else if (last && last.type === 'p') {
      last.text += ` ${line}`;
    } else {
      blocks.push({ type: 'p', text: line });
    }
  });
  return blocks.filter(Boolean);
};

// Old offers (first version) stored bilingual fields
export const jobTitle = (job) => job.title || job.title_fr || job.title_en || '';

export const CV_MAX_BYTES = 5 * 1024 * 1024;
export const CV_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

export const isLinkedInProfileUrl = (url) => /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[^/\s]+/i.test((url || '').trim());
